import { useCallback, useEffect, useRef, useState } from 'react';
import type { AutosaveStatus } from './responseShellTypes';

/**
 * Manual-save (draft) hook.
 *
 * 2026-06-22 — autosave-on-change REMOVED. Persisting on every keystroke/click
 * re-rendered the whole response body and re-ran its load effect mid-edit, which
 * "ate" radio clicks and reloaded the question grid while the user was still
 * answering. The data now lives entirely in local React state; nothing is written
 * to the server until the performer explicitly clicks Save Draft (or Submit),
 * which call flush().
 *
 * Caller still calls markDirty() on each field change — but it only flips the
 * status indicator to 'dirty' ("Unsaved changes…") so the user knows to Save
 * Draft. It does NOT schedule any network write.
 *
 * Status transitions:
 *   idle -> dirty (after markDirty — local only, no save)
 *   * -> saving (when flush() runs, i.e. Save Draft / Submit)
 *   saving -> saved | failed (after save() resolves/rejects)
 *
 * flush() performs the actual persist — used by ResponseShell's Save Draft and
 * the Submit transition.
 */
export function useAutosave(
  save: () => Promise<void>,
  _debounceMs = 1000,
): {
  status: AutosaveStatus;
  markDirty: () => void;
  flush: () => Promise<void>;
} {
  const [status, setStatus] = useState<AutosaveStatus>('idle');
  const saveRef = useRef(save);

  useEffect(() => {
    saveRef.current = save;
  }, [save]);

  const runSave = useCallback(async () => {
    setStatus('saving');
    try {
      await saveRef.current();
      setStatus('saved');
    } catch {
      setStatus('failed');
    }
  }, []);

  // Mark unsaved-changes ONLY. No debounce, no scheduled save — persistence is
  // manual (Save Draft / Submit → flush). Guard against clobbering an in-flight
  // 'saving' state. setStatus with an unchanged value is a no-op in React, so
  // repeated edits after the first do not re-render.
  const markDirty = useCallback(() => {
    setStatus(prev => (prev === 'saving' ? prev : 'dirty'));
  }, []);

  const flush = useCallback(async () => {
    await runSave();
  }, [runSave]);

  return { status, markDirty, flush };
}
